import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Flower2, User, Award, Trophy, Camera, Edit2, Save, X, ChevronLeft, Settings, BookOpen, Target, Zap, Calendar, FileText, Shield, Trash2, LogOut, Upload, Image, Download, Heart } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import BottomNav from '../components/BottomNav';
import TwoFactorSetup from '../components/TwoFactorSetup';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

// FHIR Health Data Export Section
const FhirExportSection = () => {
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleExportFHIR = async () => {
    setExporting(true);
    try {
      const res = await api.get('/users/export/fhir');
      const bundle = res.data;
      
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche_sante_fhir_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({ title: 'Export FHIR telecharge', description: `${bundle.total} ressources exportees` });
    } catch (err) {
      toast({ title: 'Erreur', description: "Impossible d'exporter les donnees", variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewFHIR = async () => {
    if (preview) { setPreview(null); return; }
    try {
      const res = await api.get('/users/export/fhir');
      const bundle = res.data;
      const entries = bundle.entry || [];
      const patient = entries.find(e => e.resource?.resourceType === 'Patient')?.resource;
      const observations = entries.filter(e => e.resource?.resourceType === 'Observation');
      const medications = entries.filter(e => e.resource?.resourceType === 'MedicationStatement');
      setPreview({ patient, observations, medications, total: bundle.total });
    } catch (err) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  return (
    <Card className="p-5 bg-white/10 backdrop-blur-md border-white/20" data-testid="fhir-export-section">
      <div className="flex items-center gap-2 mb-3">
        <Heart className="w-5 h-5 text-cyan-400" />
        <h3 className="text-lg font-bold text-white">Mes Donnees de Sante</h3>
      </div>
      <p className="text-pink-200 text-xs mb-4 leading-relaxed">
        Exportez votre fiche sante au format international HL7 FHIR R4. 
        Ce fichier contient vos cycles, symptomes et contraception codes avec SNOMED CT, 
        compatible avec les systemes hospitaliers (SIH, DPI).
      </p>

      <div className="flex gap-2 mb-3">
        <Button
          onClick={handleExportFHIR}
          disabled={exporting}
          className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-sm"
          data-testid="fhir-export-btn"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {exporting ? 'Export...' : 'Telecharger FHIR'}
        </Button>
        <Button
          onClick={handlePreviewFHIR}
          variant="outline"
          className="flex-1 border-cyan-400/30 text-cyan-200 hover:bg-cyan-500/10 text-sm"
          data-testid="fhir-preview-btn"
        >
          <FileText className="w-4 h-4 mr-1.5" />
          {preview ? 'Masquer' : 'Apercu'}
        </Button>
      </div>

      {/* Standards badges */}
      <div className="flex gap-2 mb-3">
        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-[10px] rounded-full font-medium">HL7 FHIR R4</span>
        <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-[10px] rounded-full font-medium">SNOMED CT</span>
        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] rounded-full font-medium">Anonymise</span>
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-black/20 rounded-lg p-3 space-y-3 text-xs" data-testid="fhir-preview-content">
          <div className="flex items-center justify-between">
            <span className="text-cyan-300 font-semibold">Bundle FHIR R4</span>
            <span className="text-gray-400">{preview.total} ressources</span>
          </div>

          {/* Patient */}
          {preview.patient && (
            <div className="p-2 bg-white/5 rounded">
              <p className="text-blue-300 font-medium mb-1">Patient</p>
              <p className="text-white">{preview.patient.name?.[0]?.given?.[0]} {preview.patient.name?.[0]?.family}</p>
              <p className="text-gray-400">Genre: {preview.patient.gender} | Pays: {preview.patient.address?.[0]?.country || 'CI'}</p>
            </div>
          )}

          {/* Observations summary */}
          {preview.observations.length > 0 && (
            <div className="p-2 bg-white/5 rounded">
              <p className="text-pink-300 font-medium mb-1">Observations ({preview.observations.length})</p>
              {preview.observations.slice(0, 4).map((o, i) => (
                <div key={i} className="flex items-center justify-between py-0.5">
                  <span className="text-white truncate">{o.resource.code?.text || 'Observation'}</span>
                  <span className="text-gray-400 text-[10px]">{o.resource.effectiveDateTime}</span>
                </div>
              ))}
              {preview.observations.length > 4 && (
                <p className="text-gray-500 text-center mt-1">+{preview.observations.length - 4} autres...</p>
              )}
            </div>
          )}

          {/* Medications */}
          {preview.medications.length > 0 && (
            <div className="p-2 bg-white/5 rounded">
              <p className="text-purple-300 font-medium mb-1">Contraception ({preview.medications.length})</p>
              {preview.medications.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-0.5">
                  <span className="text-white">{m.resource.medicationCodeableConcept?.text}</span>
                  <span className={`text-[10px] ${m.resource.status === 'active' ? 'text-green-400' : 'text-gray-500'}`}>
                    {m.resource.status === 'active' ? 'Actif' : 'Arrete'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {preview.observations.length === 0 && preview.medications.length === 0 && (
            <p className="text-gray-400 text-center py-2">Aucune donnee de sante enregistree</p>
          )}
        </div>
      )}
    </Card>
  );
};

// Historical African Women Avatars with descriptions
const Profile = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, updateUser, logout, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const unearnedBadges = useMemo(() => allBadges.filter(b => !b.earned).slice(0, 6), [allBadges]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, authLoading]);

  const fetchProfile = async () => {
    try {
      const [profileRes, badgesRes] = await Promise.all([
        api.get('/users/profile/full'),
        api.get('/users/badges')
      ]);

      setProfile(profileRes.data.user);
      setStats(profileRes.data.stats);
      setBadges(profileRes.data.badges);
      setAllBadges(badgesRes.data.allBadges);
      setEditForm({
        firstName: profileRes.data.user.firstName,
        lastName: profileRes.data.user.lastName
      });

      // Check for new badges
      await api.post('/users/check-badges');
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le profil',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await userAPI.updateProfile(editForm);
      setProfile(prev => ({ ...prev, ...editForm }));
      updateUser({ ...authUser, ...editForm });
      setEditing(false);
      toast({
        title: 'Profil mis à jour',
        description: 'Vos modifications ont été enregistrées'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder',
        variant: 'destructive'
      });
    }
  };

  const handleUploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une image',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Erreur',
        description: 'L\'image ne doit pas dépasser 5 Mo',
        variant: 'destructive'
      });
      return;
    }

    setUploadingAvatar(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        try {
          const response = await api.post('/users/avatar/upload', { 
            image: base64Data,
            filename: file.name
          });
          
          setProfile(prev => ({ ...prev, avatar: response.data.avatarUrl, avatarInfo: null }));
          updateUser({ ...authUser, avatar: response.data.avatarUrl });
          setShowAvatarDialog(false);
          toast({
            title: 'Photo de profil mise à jour',
            description: 'Votre avatar personnalisé a été enregistré'
          });
        } catch (error) {
          toast({
            title: 'Erreur',
            description: error.response?.data?.detail || 'Impossible d\'uploader l\'image',
            variant: 'destructive'
          });
        } finally {
          setUploadingAvatar(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadingAvatar(false);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la lecture du fichier',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast({
        title: 'Erreur',
        description: 'Veuillez taper SUPPRIMER pour confirmer',
        variant: 'destructive'
      });
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/users/account');
      toast({
        title: 'Compte supprimé',
        description: 'Votre compte a été supprimé avec succès'
      });
      logout();
      navigate('/');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le compte',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!profile) return null;

  const progressPercent = (profile.points % 1000) / 10;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-white">Mon Profil</h1>
          <button 
            onClick={() => navigate('/notifications')} 
            className="text-white p-2"
            data-testid="profile-settings-btn"
          >
            <Settings className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Profile Header Card */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex flex-col items-center mb-6">
            {/* Avatar */}
            <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
              <DialogTrigger asChild>
                <button className="relative mb-4 group" data-testid="change-avatar-btn">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-pink-400 shadow-lg">
                    {profile.avatar ? (
                      <img 
                        src={profile.avatar} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
                        {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center shadow-lg group-hover:bg-pink-600 transition-colors">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white text-xl">Photo de profil</DialogTitle>
                </DialogHeader>
                
                <div className="text-center py-6">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-dashed border-pink-400/50 flex items-center justify-center bg-white/5">
                    {uploadingAvatar ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
                    ) : (
                      <Upload className="w-10 h-10 text-pink-400" />
                    )}
                  </div>
                  <p className="text-pink-200 text-sm mb-4">
                    Téléchargez votre propre photo de profil
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUploadAvatar}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                    data-testid="avatar-upload-btn"
                  >
                    <Image className="w-4 h-4 mr-2" />
                    {uploadingAvatar ? 'Chargement...' : 'Choisir une image'}
                  </Button>
                  <p className="text-pink-200/60 text-xs mt-3">
                    Formats acceptés : JPG, PNG, GIF (max 5 Mo)
                  </p>
                </div>
              </DialogContent>
            </Dialog>

            {/* Name */}
            {editing ? (
              <div className="w-full space-y-3">
                <Input
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  placeholder="Prénom"
                  className="bg-white/10 border-white/20 text-white text-center"
                />
                <Input
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  placeholder="Nom"
                  className="bg-white/10 border-white/20 text-white text-center"
                />
                <div className="flex gap-2 justify-center">
                  <Button onClick={handleSaveProfile} className="bg-pink-500 hover:bg-pink-600">
                    <Save className="w-4 h-4 mr-1" /> Enregistrer
                  </Button>
                  <Button onClick={() => setEditing(false)} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white mb-1">
                  {profile.firstName} {profile.lastName}
                </h2>
                <p className="text-pink-200 text-sm mb-2">{profile.email}</p>
                <Button 
                  onClick={() => setEditing(true)} 
                  variant="ghost" 
                  size="sm"
                  className="text-pink-300 hover:text-white hover:bg-white/10"
                  data-testid="edit-profile-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" /> Modifier
                </Button>
              </div>
            )}
          </div>

          {/* Level Progress */}
          <div className="bg-white/5 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-pink-200 text-sm">Niveau {profile.level}</span>
              <span className="text-white font-bold">{profile.points} pts</span>
            </div>
            <Progress value={progressPercent} className="h-3 bg-purple-900/50" />
            <p className="text-pink-300 text-xs mt-2 text-center">
              {1000 - (profile.points % 1000)} points pour le niveau suivant
            </p>
          </div>
        </Card>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-400/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-300" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{stats.completedModules}</p>
                  <p className="text-blue-200 text-xs">Modules complétés</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-400/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-300" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{stats.passedQuizzes}</p>
                  <p className="text-green-200 text-xs">Quiz réussis</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-400/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-orange-300" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{stats.currentStreak}</p>
                  <p className="text-orange-200 text-xs">Jours de série</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-400/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">{Math.round(stats.avgQuizScore)}%</p>
                  <p className="text-purple-200 text-xs">Score moyen</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Badges Section */}
        <Card className="p-5 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-bold text-white">Mes Badges</h3>
            </div>
            <span className="text-pink-200 text-sm">{badges.length}/{allBadges.length}</span>
          </div>

          {badges.length > 0 ? (
            <div className="grid grid-cols-4 gap-3 mb-4">
              {badges.map((badge) => (
                <div 
                  key={badge.id || badge.name}
                  className="flex flex-col items-center p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  title={badge.description}
                >
                  <span className="text-3xl mb-1">{badge.icon}</span>
                  <span className="text-white text-xs text-center font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-pink-200 text-sm text-center py-4">
              Complétez des modules et des quiz pour gagner des badges!
            </p>
          )}

          {/* All badges preview */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <p className="text-pink-300 text-xs mb-3">Badges à débloquer:</p>
            <div className="flex flex-wrap gap-2">
              {unearnedBadges.map((badge) => (
                <div 
                  key={badge.id || badge.name}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 opacity-50"
                  title={badge.description}
                >
                  <span className="text-lg grayscale">{badge.icon}</span>
                  <span className="text-white text-xs">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Member Since */}
        <Card className="p-4 bg-white/5 border-white/10">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-pink-300" />
            <div>
              <p className="text-white text-sm">Membre depuis</p>
              <p className="text-pink-200 text-xs">
                {profile.joinDate ? new Date(profile.joinDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                }) : 'Récemment'}
              </p>
            </div>
          </div>
        </Card>

        {/* FHIR Health Data Export */}
        <FhirExportSection />

        {/* 2FA Security */}
        <TwoFactorSetup />

        {/* Legal Links */}
        <Card className="p-4 bg-white/5 border-white/10">
          <h3 className="text-white font-semibold mb-3">Informations légales</h3>
          <div className="space-y-2">
            <Link 
              to="/terms" 
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <FileText className="w-5 h-5 text-pink-300" />
              <span className="text-pink-100 text-sm">Conditions d'utilisation</span>
            </Link>
            <Link 
              to="/privacy" 
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <Shield className="w-5 h-5 text-pink-300" />
              <span className="text-pink-100 text-sm">Politique de confidentialité</span>
            </Link>
            <Link 
              to="/security" 
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <Shield className="w-5 h-5 text-pink-300" />
              <span className="text-pink-100 text-sm">Sécurité</span>
            </Link>
          </div>
        </Card>

        {/* Account Actions */}
        <Card className="p-4 bg-white/5 border-white/10">
          <h3 className="text-white font-semibold mb-3">Compte</h3>
          <div className="space-y-2">
            <button 
              onClick={() => { logout(); navigate('/'); }}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors w-full text-left"
            >
              <LogOut className="w-5 h-5 text-pink-300" />
              <span className="text-pink-100 text-sm">Déconnexion</span>
            </button>
            <button 
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-red-500/10 transition-colors w-full text-left"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
              <span className="text-red-300 text-sm">Supprimer mon compte</span>
            </button>
          </div>
        </Card>

        {/* Delete Account Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-purple-900 border-purple-700">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">Supprimer le compte</DialogTitle>
              <DialogDescription className="text-pink-200">
                Cette action est irréversible. Toutes vos données seront définitivement supprimées.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white text-sm mb-3">
                Pour confirmer, tapez <span className="font-bold text-red-400">SUPPRIMER</span> ci-dessous :
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="SUPPRIMER"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? 'Suppression...' : 'Supprimer définitivement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <BottomNav active="profile" />
    </div>
  );
};

export default Profile;
